import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  Book as BookIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { bookService } from '../../services/bookService';
import { currencyService } from '../../services/currencyService';
import { format } from 'date-fns';

function CollectionInsights() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [userCurrency, setUserCurrency] = useState('THB');
  const [insights, setInsights] = useState({
    totalBooks: 0,
    totalValue: 0,
    averagePrice: 0,
    totalQuantity: 0,
    ownedBooks: 0,
    preorderBooks: 0,
    ownedValue: 0,
    preorderValue: 0
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [booksData, currency] = await Promise.all([
        bookService.getBooks(currentUser.uid),
        currencyService.getUserCurrency(currentUser.uid)
      ]);
      
      setBooks(booksData);
      setUserCurrency(currency || 'THB');
      
      // Calculate insights
      const ownedBooks = booksData.filter(book => book.createdDate);
      const preorderBooks = booksData.filter(book => !book.createdDate);
      
      const ownedValue = ownedBooks.reduce((sum, book) => sum + (book.price || 0), 0);
      const preorderValue = preorderBooks.reduce((sum, book) => sum + (book.preorder_price || 0), 0);
      
      const totalValue = ownedValue + preorderValue;
      const averagePrice = booksData.length > 0 ? totalValue / booksData.length : 0;
      const totalQuantity = booksData.reduce((sum, book) => sum + (book.qty || 1), 0);
      
      setInsights({
        totalBooks: booksData.length,
        totalValue,
        averagePrice,
        totalQuantity,
        ownedBooks: ownedBooks.length,
        preorderBooks: preorderBooks.length,
        ownedValue,
        preorderValue
      });
    } catch (error) {
      console.error('Error loading insights data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser, loadData]);

  const getTopCategories = (field) => {
    const counts = {};
    books.forEach(book => {
      const value = book[field];
      if (value) {
        counts[value] = (counts[value] || 0) + 1;
      }
    });
    
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([value, count]) => ({ value, count }));
  };

  const getMostValuableBooks = () => {
    return books
      .filter(book => book.price)
      .sort((a, b) => (b.price || 0) - (a.price || 0))
      .slice(0, 5);
  };

  const getRecentAdditions = () => {
    return books
      .filter(book => book.createdDate)
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
      .slice(0, 5);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          {t('insights.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('insights.subtitle')}
        </Typography>
      </Box>

      {/* Overview Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BookIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {insights.totalBooks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('insights.stats.totalItems')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MoneyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {userCurrency} {insights.totalValue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('insights.stats.totalValue')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InventoryIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {insights.totalQuantity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('insights.stats.totalQuantity')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {userCurrency} {insights.averagePrice.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('insights.stats.averagePrice')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Collection Status */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('insights.overview.collectionStatus')}
              </Typography>
              
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    {t('insights.overview.ownedItems')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {insights.ownedBooks} ({userCurrency} {insights.ownedValue.toLocaleString()})
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(insights.ownedBooks / insights.totalBooks) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    {t('insights.overview.preorderItems')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {insights.preorderBooks} ({userCurrency} {insights.preorderValue.toLocaleString()})
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(insights.preorderBooks / insights.totalBooks) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('insights.overview.valueBreakdown')}
              </Typography>
              
              <Box display="flex" alignItems="center" mb={2}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'primary.main', borderRadius: '50%', mr: 2 }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {t('insights.overview.ownedValue')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {userCurrency} {insights.ownedValue.toLocaleString()}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center">
                <Box sx={{ width: 12, height: 12, bgcolor: 'secondary.main', borderRadius: '50%', mr: 2 }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {t('insights.overview.preorderValue')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {userCurrency} {insights.preorderValue.toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Categories */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('insights.categories.topPublishers')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {getTopCategories('publisher').map(({ value, count }) => (
                  <Chip 
                    key={value} 
                    label={`${value} (${count})`} 
                    color="primary" 
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('insights.categories.topGenres')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {getTopCategories('genre').map(({ value, count }) => (
                  <Chip 
                    key={value} 
                    label={`${value} (${count})`} 
                    color="primary" 
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Most Valuable Books */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {t('insights.overview.mostValuableItems')}
          </Typography>
          <List>
            {getMostValuableBooks().map((book, index) => (
              <React.Fragment key={book.id}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <StarIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={book.title}
                    secondary={`${book.author} • ${book.publisher}`}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {userCurrency} {(book.price || 0).toLocaleString()}
                  </Typography>
                </ListItem>
                {index < getMostValuableBooks().length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Recent Additions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {t('insights.recent.recentAdditions')}
          </Typography>
          <List>
            {getRecentAdditions().map((book, index) => (
              <React.Fragment key={book.id}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <BookIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={book.title}
                    secondary={`${book.author} • ${format(new Date(book.createdDate), 'MMM dd, yyyy')}`}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {userCurrency} {(book.price || 0).toLocaleString()}
                  </Typography>
                </ListItem>
                {index < getRecentAdditions().length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Footer */}
      <Box sx={{ mt: 6, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          BookPort
        </Typography>
        <Typography variant="caption">
          © 2024 BookPort. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
}

export default CollectionInsights; 